import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type {
  FilterCategory,
  Filter,
  NormalizedFilters,
} from "@interfaces/filters";
import { fetchFiltersByMarketId } from "./filters.thunk";

const categoriesAdapter = createEntityAdapter<FilterCategory, number>({
  // @ts-ignore
  selectId: (category: FilterCategory) => category.id,
  sortComparer: (a: FilterCategory, b: FilterCategory) =>
    a.categoryOrder - b.categoryOrder,
});

const filtersAdapter = createEntityAdapter<Filter, number>({
  // @ts-ignore
  selectId: (filter: Filter) => filter.id,
  sortComparer: (a: Filter, b: Filter) => a.filterOrder - b.filterOrder,
});

interface FiltersState {
  categories: ReturnType<typeof categoriesAdapter.getInitialState>;
  filters: ReturnType<typeof filtersAdapter.getInitialState>;
  loading: boolean;
  error: string | null;
}

const initialState: FiltersState = {
  categories: categoriesAdapter.getInitialState(),
  filters: filtersAdapter.getInitialState(),
  loading: false,
  error: null,
};

export const filtersSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFiltersByMarketId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchFiltersByMarketId.fulfilled,
        (state, action: PayloadAction<NormalizedFilters>) => {
          state.loading = false;
          state.error = null;
          categoriesAdapter.setAll(state.categories, action.payload.categories);
          filtersAdapter.setAll(state.filters, action.payload.filters);
        }
      )
      .addCase(fetchFiltersByMarketId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load filters";
      });
  },
});

export type FiltersAdapter = EntityState<Filter, number>;

export type FilterCategoriesAdapter = EntityState<FilterCategory, number>;

export type FiltersAdapterType = {
  filters: EntityState<Filter, number>;
  categories: EntityState<FilterCategory, number>;
};

export default filtersSlice.reducer;
